import { useTranslation } from 'react-i18next';
import { PageHeader } from '@/components/core';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui-kit/tabs';
import { PROJECTS_TABS, ProjectsTab } from '@/constant/app-paths';
import { useHasPermission } from '@/hooks/use-has-permission';
import { useUrlTab } from '@/hooks/use-linked-record';
import { MbmProjectsPanel } from '@/modules/settings/organization/mbm-projects';
import { KosovoProjectsPanel } from '../project-manager/project-manager';

/**
 * Settings › Projects (HR Module Navigation Map; Phase 8): tabs Kosovo Projects (managed here) and MBM
 * Projects (synced from MBM, read-only), each for whoever holds its permissions. The tab is in the URL
 * (`?tab=`); the old MBM Projects page redirects to its tab.
 */
export const ProjectsPage = ({
  kosovoPermissions,
  mbmPermissions,
}: {
  kosovoPermissions: string[];
  mbmPermissions: string[];
}) => {
  const { t } = useTranslation();
  const canKosovo = useHasPermission(kosovoPermissions);
  const canMbm = useHasPermission(mbmPermissions);
  const tabs = [
    ...(canKosovo ? [PROJECTS_TABS.KOSOVO] : []),
    ...(canMbm ? [PROJECTS_TABS.MBM] : []),
  ];
  const [tab, setTab] = useUrlTab<ProjectsTab>(tabs, tabs[0] ?? PROJECTS_TABS.KOSOVO);

  return (
    <div className="flex w-full flex-col gap-4">
      <PageHeader title={t('PROJECTS')} />
      <Tabs value={tab} onValueChange={(value) => setTab(value as ProjectsTab)}>
        <TabsList className="max-sm:w-full">
          {canKosovo && (
            <TabsTrigger value={PROJECTS_TABS.KOSOVO} className="max-sm:flex-1">
              {t('KOSOVO_PROJECTS')}
            </TabsTrigger>
          )}
          {canMbm && (
            <TabsTrigger value={PROJECTS_TABS.MBM} className="max-sm:flex-1">
              {t('MBM_PROJECTS')}
            </TabsTrigger>
          )}
        </TabsList>
        {canKosovo && (
          <TabsContent value={PROJECTS_TABS.KOSOVO} className="mt-4">
            <KosovoProjectsPanel />
          </TabsContent>
        )}
        {canMbm && (
          <TabsContent value={PROJECTS_TABS.MBM} className="mt-4">
            <MbmProjectsPanel />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};
